import React, {
	useState,
	useMemo,
	useCallback,
} from 'react';
import {
	Icon,
	IconProp,
} from '@fortawesome/fontawesome-svg-core';
import {
	faPlus,
	faMinus,
} from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Row,
	Col,
	Divider,
	Space,
	Popover,
	Button,
} from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from 'react-query';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import {
	ContractCompletionTypes,
	VesselCostType,
	DATE_AND_TIME,
	FeatureFlags,
	FixtureTypes,
	NumberFormatStyle,
	VesselOwnershipTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import isItemPortCall from '@shared/utils/isItemPortCall';
import Card from '@client/components/Card/Card';
import Table from '@client/components/Table/Table';
import { useBunkerModal } from '@client/utils/hooks/useBunkerModal';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import { formatDate } from '@client/utils/formatDate';
import { generateVcPnlPdf, getVcPnl } from '@client/lib/api';
import CsvExport from '@client/components/Export/CsvExport/CsvExport';
import useNumberFormatStyle from '@client/utils/hooks/useExportNotation';
import ExportNotationSelect from '@client/components/Export/ExportNotation/ExportNotationSelect';
import getVcPnlItems from '../ProfitAndLossTab/helpers/getVcPnlItems';
import getPnlTableColumns from '../ProfitAndLossTab/helpers/pnlTableColumns';
import { useVoyage } from '../VoyageProvider/VoyageProvider';
import PnlOverride from '../ProfitAndLossTab/PnlOverride';
import { buildExportPnlBaseSection, formatPercent, handleNotation } from '../../helpers/pnlCsvExport';
import { useItinerary } from '../ItineraryTab/ItineraryProvider';
import styles from './VcPnl.module.css';

export type TablePnlEntry = {
	type: string;
	item: string;
	actual: number;
	estimated: number;
	invoiced: number;
	bold?: boolean;
	highlight?: boolean;
	number?: boolean;
	vesselCostType?: Values<typeof VesselCostType>;
	onClick?: () => void;
	actionTestId?: string;
	icon?: Icon;
	iconTooltip?: string;
	iconColor?: string;
};

type Props = {
	columnsToHide?: ('actual' | 'estimated' | 'invoiced' | 'variance')[];
	spanOverride?: {
		xs?: number;
		sm?: number;
		xl?: number;
		xxl?: number;
	};
	slim?: boolean;
	hideHeaders?: boolean;
};

const VcPnl = ({
	columnsToHide = [],
	spanOverride = {},
	slim,
	hideHeaders,
}: Props) => {
	const {
		voyageId,
		fixtureCurrency,
	} = useVoyage();

	const {
		data: pnl,
	} = useQuery(
		['vcPnl', voyageId],
		() => getVcPnl(voyageId),
		{ enabled: voyageId != null },
	);

	const isAlba = useFeatureFlag(FeatureFlags.ALBA);
	const [activeTab, setActiveTab] = useState('pnl');
	const { formatStyle, setFormatStyle } = useNumberFormatStyle(NumberFormatStyle.UkUs);
	const showActual = !columnsToHide.includes('actual');
	const showEstimated = !columnsToHide.includes('estimated');
	const showInvoiced = !columnsToHide.includes('invoiced');
	const columns = getPnlTableColumns(fixtureCurrency, columnsToHide);
	const { open, modal } = useBunkerModal();

	const {
		voyageDetails,
		hasCargoRelet,
		vendorExpenses,
	} = useVoyage();

	const euEtsExpense = vendorExpenses?.rows.find((expense) => expense.isEuEts);
	const isEuEtsIceClassDiscounted = euEtsExpense?.isIceClass === true && euEtsExpense.overrideAmount == null;

	const { localItinerary } = useItinerary();

	const bunkersGoBelowZero = useMemo(() => (
		localItinerary.some((entry) => {
			if (!isItemPortCall(entry)) {
				return false;
			}

			return entry.PortCallActions.some((action) => (
				action.RobBunkers.some((rb) => (
					(rb.forecasted ? rb.forecastedQuantity : rb.totalQuantity) < 0
				))
			));
		})
	), [localItinerary]);

	const exportToPdfMutation = useMutation(
		generateVcPnlPdf,
		{
			onSuccess: (data) => {
				const url = window.URL.createObjectURL(data);
				const link = document.createElement('a');
				link.href = url;

				link.download = `voyage-pnl-report-${voyageDetails.id}.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			},
		},
	);

	const handleExport = useCallback(async () => {
		exportToPdfMutation.mutate({
			voyageId: voyageDetails.id,
		});
	}, [exportToPdfMutation, voyageDetails.id]);

	const { revenueItems, expenseItems, resultsItems } = useMemo(() => {
		if (pnl == null) {
			return {
				revenueItems: [],
				expenseItems: [],
				resultsItems: [],
			};
		}

		const showTcInDeliveryBunkers = (
			voyageDetails.completionType === ContractCompletionTypes.TC_IN_DELIVERY
		);

		const items = getVcPnlItems(
			pnl,
			() => open(FixtureTypes.TC_IN),
			showTcInDeliveryBunkers,
			bunkersGoBelowZero,
			hasCargoRelet,
			voyageDetails.vessel.ownershipType === VesselOwnershipTypes.TC_IN,
			isEuEtsIceClassDiscounted,
		);

		return Object.entries(items).reduce((acc, [key, value]) => {
			const mapped = value.map((v) => {
				return {
					item: v.item,
					// @ts-ignore THIS IS DUMB
					number: v?.number,
					// @ts-ignore
					bold: v?.bold,
					// @ts-ignore
					onClick: v?.onClick,
					// @ts-ignore
					icon: v?.icon,
					// @ts-ignore
					iconColor: v?.iconColor,
					// @ts-ignore
					iconTooltip: v?.iconTooltip,
					// @ts-ignore
					highlight: v?.highlight,
					...(showEstimated ? { estimated: v.estimated } : []),
					...(showActual ? { actual: v.actual } : []),
					...(showInvoiced ? { invoiced: v.invoiced } : []),
				};
			});

			return {
				...acc,
				[key]: mapped,
			};
		}, {
			revenueItems: [],
			expenseItems: [],
			resultsItems: [],
		});
	}, [
		pnl,
		voyageDetails.completionType,
		voyageDetails.vessel.ownershipType,
		open,
		bunkersGoBelowZero,
		showEstimated,
		showActual,
		showInvoiced,
		hasCargoRelet,
		isEuEtsIceClassDiscounted,
	]);

	const buildExportPnlSection = (sectionTitle: string, rows: TablePnlEntry[]) => {
		if (rows.length === 0) {
			return [];
		}

		const baseSection = buildExportPnlBaseSection(
			sectionTitle,
			['Description', 'Estimated', 'Actual', 'Variance', '%'],
		);

		const bodyRows = rows.map((row) => {
			const variance = row.actual - row.estimated;
			const variancePercentage = variance / (row.estimated || 1);

			return [
				row.item,
				handleNotation(row.estimated, formatStyle),
				handleNotation(row.actual, formatStyle),
				handleNotation(variance, formatStyle),
				formatPercent(variancePercentage * 100, formatStyle),
			];
		});

		return [
			...baseSection,
			...bodyRows,
			[],
		];
	};

	const csvData = useMemo(() => {
		const titleRow = [`ClearVoyage VC PNL export ${formatDate(new Date(), DATE_AND_TIME)}`];
		const currencyRow = [`Contract Currency: ${fixtureCurrency}`];

		return [
			titleRow,
			currencyRow,
			[],
			...buildExportPnlSection('Revenue', revenueItems),
			...buildExportPnlSection('Expenses', expenseItems),
			...buildExportPnlSection('Results', resultsItems),
		];
	}, [revenueItems, expenseItems, resultsItems, voyageDetails.id, formatStyle, fixtureCurrency]);
	let content: React.ReactNode;

	if (activeTab === 'alba') {
		content = (<PnlOverride voyageId={voyageDetails.id} />);
	} else {
		content = (
			<>
				{!hideHeaders && (
					<Divider>
						<Space>
							<FontAwesomeIcon icon={faPlus as IconProp} />
							Revenue
						</Space>
					</Divider>
				)}
				<Table<TablePnlEntry>
					data-testid="revenueTable"
					size="small"
					pagination={false}
					columns={columns}
					dataSource={revenueItems}
					className={styles.table}
					bordered
					showHeader={!hideHeaders}
				/>
				{!hideHeaders && (
					<Divider>
						<Space>
							<FontAwesomeIcon icon={faMinus as IconProp} />
							Expenses
						</Space>
					</Divider>
				)}
				<Table<TablePnlEntry>
					data-testid="expensesTable"
					size="small"
					pagination={false}
					columns={columns}
					dataSource={expenseItems}
					className={styles.table}
					bordered
					showHeader={!hideHeaders}
				/>
				{!hideHeaders && (<Divider>Results</Divider>)}
				<Table<TablePnlEntry>
					data-testid="resultTable"
					size="small"
					pagination={false}
					columns={columns}
					dataSource={resultsItems}
					className={styles.table}
					bordered
					showHeader={!hideHeaders}
				/>
			</>
		);
	}

	return (
		<>
			{modal}
			<Row align="middle" justify="center">
				<Col
					xs={spanOverride.xs ?? 24}
					sm={spanOverride.sm ?? 24}
					xl={spanOverride.xl ?? 20}
					xxl={spanOverride.xxl ?? 12}
				>
					<Card
						slim={slim}
						bodyStyle={{ paddingTop: 0 }}
						title={hideHeaders ? null : 'P&L Sheet'}
						extra={activeTab === 'pnl' && (
							<>
								<Popover
									content={(
										<Space direction="vertical">
											<ExportNotationSelect
												value={formatStyle}
												onChange={setFormatStyle}
												label="Number format"
											/>
											<div style={{ display: 'flex', gap: 12 }}>
												<CsvExport
													data={csvData}
													filename={`ClearVoyage VC PNL export ${formatDate(new Date(), DATE_AND_TIME)}.csv`}
													buttonLabel="Export P&L (CSV)"
												/>
												<Button
													block
													icon={<FontAwesomeIcon icon={faFilePdf} />}
													type="primary"
													onClick={handleExport}
													loading={exportToPdfMutation.isLoading}
												>
													Export Voyage Report (PDF)
												</Button>
											</div>
										</Space>
									)}
								>
									<Button icon={<ExportOutlined />}>Export</Button>
								</Popover>
							</>
						)}
						tabList={isAlba ? [
							{ label: 'P&L', key: 'pnl' },
							{ label: 'P&L Overrides', key: 'alba' },
						] : undefined}
						onTabChange={(key) => setActiveTab(key)}
					>
						{content}
					</Card>
				</Col>
			</Row>
		</>

	);
};

export default VcPnl;
